import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { PUBLIC_KEY_JWK, DIAS_TRIAL } from '../config/licencia'
import { obtenerLicencia, registrarInstalacion, guardarLicenciaActivada } from '../db/licencia'

const LicenciaContext = createContext(null)

/**
 * estados posibles:
 *  'cargando'  — leyendo de DB al inicio
 *  'trial'     — dentro del periodo de prueba
 *  'expirado'  — trial terminado, sin licencia activa
 *  'activo'    — licencia válida (plan basico o sync)
 *  'vencido'   — licencia sync con fecha de expiración pasada
 */
export function LicenciaProvider({ children }) {
  const [estado, setEstado] = useState('cargando')
  const [licencia, setLicencia] = useState(null)
  const [diasRestantes, setDiasRestantes] = useState(null)
  const [error, setError] = useState(null)

  const cargar = useCallback(async () => {
    await registrarInstalacion()
    const { instaladoEn, licencia: lic } = await obtenerLicencia()

    if (lic) {
      // Hay licencia activada — verificar expiración (solo plan sync)
      if (lic.expira && new Date(lic.expira) < new Date()) {
        setLicencia(lic)
        setEstado('vencido')
      } else {
        setLicencia(lic)
        setEstado('activo')
      }
      return
    }

    // Sin licencia — calcular días de trial
    const instalado = new Date(instaladoEn)
    const hoy = new Date()
    const diasUsados = Math.floor((hoy - instalado) / (1000 * 60 * 60 * 24))
    const restantes = DIAS_TRIAL - diasUsados

    setDiasRestantes(Math.max(0, restantes))

    if (restantes > 0) {
      setEstado('trial')
    } else {
      setEstado('expirado')
    }
  }, [])

  useEffect(() => { cargar() }, [cargar])

  /** Verifica y activa un código de activación. Lanza Error si es inválido. */
  async function activar(codigo) {
    setError(null)
    const partes = codigo.trim().replace(/\s+/g, '').split('.')
    if (partes.length !== 2) throw new Error('Formato de código inválido.')

    const [payloadB64, firmaB64] = partes

    // Importar clave pública
    const clavePublica = await crypto.subtle.importKey(
      'jwk', PUBLIC_KEY_JWK,
      { name: 'ECDSA', namedCurve: 'P-256' },
      false,
      ['verify']
    )

    // Decodificar firma
    const firmaBuffer = Uint8Array.from(
      atob(firmaB64.replace(/-/g, '+').replace(/_/g, '/')),
      c => c.charCodeAt(0)
    )

    // Verificar firma ECDSA
    const valido = await crypto.subtle.verify(
      { name: 'ECDSA', hash: 'SHA-256' },
      clavePublica,
      firmaBuffer,
      new TextEncoder().encode(payloadB64)
    )

    if (!valido) throw new Error('El código no es válido o fue alterado.')

    // Decodificar payload
    const datos = JSON.parse(atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/')))

    // Verificar expiración (plan sync)
    if (datos.expira && new Date(datos.expira) < new Date()) {
      throw new Error('Este código ya expiró. Contacta a Practicaja para renovar.')
    }

    // Guardar y actualizar estado
    await guardarLicenciaActivada(datos)
    setLicencia(datos)
    setEstado('activo')

    return datos
  }

  return (
    <LicenciaContext.Provider value={{ estado, licencia, diasRestantes, activar, error, recargar: cargar }}>
      {children}
    </LicenciaContext.Provider>
  )
}

export function useLicencia() {
  return useContext(LicenciaContext)
}
